import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { useSegwayRouter } from '@/features/shared/hooks/useSegwayRouter'
import { Controller, useForm } from 'react-hook-form'
import { Body } from '@shipt/design-system-typography'
import { type RedeemEmailFormData } from '@/features/cms/components/CMS/types'
import {
  CMSFormInputWrapper,
  CMSFormSubmitButton,
} from '@/features/cms/components/CMS/components/Forms/styles'
import { routes } from '@shared/constants/routes'
import { getErrorStatus } from '@/features/shared/utils/dataFetching/utils'
import { useMutationCheckUserEmail } from '@/features/account/services/User/mutations'
import { useCMSTrackingEvents } from '@/features/cms/components/CMS/CMSContext'
import { getCMSWrapperProps } from '@/features/cms/components/CMS/utils/getCMSWrapperProps'
import { TextInput } from '@shipt/design-system-inputs'
import { Box, Stack } from '@shipt/design-system-layouts'

type EmailForm = { email: string }

const emailFormValidation: z.Schema<EmailForm> = z.object({
  email: z.string().min(1, 'Email is required').email('Please enter a valid email address'),
})

/**
 * This component corresponds to the `redeem_email_form` content type in the CMS.
 *
 * - **Staging** - https://cms-staging.shipt.com/content-type/pool/redeem_email_form
 * - **Production** - https://cms.shipt.com/content-type/pool/redeem_email_form
 */
export const RedeemEmailForm = ({
  content_type_id,
  id,
  data: { disclaimer, button_treatment = 'action' },
}: RedeemEmailFormData) => {
  const router = useSegwayRouter()
  const { trackCMSElementClicked } = useCMSTrackingEvents()
  const { mutateAsync: checkUserEmail, isPending } = useMutationCheckUserEmail()
  const {
    handleSubmit,
    control,
    formState: { errors },
    clearErrors,
  } = useForm<EmailForm>({
    resolver: zodResolver(emailFormValidation),
    defaultValues: { email: '' },
    reValidateMode: 'onSubmit',
  })

  const onSubmit = async ({ email }: EmailForm) => {
    if (!email) return

    trackCMSElementClicked({ content: 'redeem', type: 'button' })
    try {
      const { redeem_flow } = await checkUserEmail(email)

      if (redeem_flow === 'credit') {
        router.push(`${routes.REDEEM_LOGIN.url}?redeem=credit`)
      }
      if (redeem_flow === 'subscription') {
        router.push(`${routes.REDEEM_SIGNUP.url}?redeem=membership`)
      }
    } catch (e) {
      // 404 means that the user does not have an account yet
      if (getErrorStatus(e) === 404) {
        router.push(`${routes.REDEEM_SIGNUP.url}?redeem=membership`)
      }
    }
  }

  return (
    <form
      {...getCMSWrapperProps({ content_type_id, id })}
      onSubmit={handleSubmit(onSubmit)}
      noValidate
    >
      <CMSFormInputWrapper align="center" justify="center">
        <Stack
          margin={{ md: [0, 'lg', 0, '4xl'] }}
          direction={{ base: 'column', md: 'row' }}
          spacing={{ base: 'sm', md: 'md' }}
          flexGrow={1}
        >
          <Box flexGrow={1}>
            <Controller
              control={control}
              name="email"
              render={({ field }) => (
                <TextInput
                  {...field}
                  onChange={(e) => {
                    field.onChange(e)
                    if (errors.email) {
                      clearErrors('email')
                    }
                  }}
                  aria-label="Email"
                  name="email"
                  id="email"
                  type="email"
                  placeholder="Email"
                  size="lg"
                  errorMessage={errors.email?.message}
                />
              )}
            />
          </Box>
          <Box>
            <CMSFormSubmitButton
              type="submit"
              loading={isPending}
              disabled={isPending}
              concept={button_treatment}
            >
              Get started
            </CMSFormSubmitButton>
          </Box>
        </Stack>
      </CMSFormInputWrapper>
      {!!disclaimer && <Body size="sm">{disclaimer}</Body>}
    </form>
  )
}
